import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Phone, Video, Mic, MicOff, VideoOff, Loader, ArrowLeft, CheckCircle } from 'lucide-react';
import { ConsultationPermissionsGate } from '../components/ConsultationPermissionsGate';
import { SessionTimer } from '../components/SessionTimer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Patient-side page for a psychology call INITIATED BY THE DOCTOR.
 * Route: /psychology/join-call/:callId
 *
 * The patient reaches this page from an in-app or push notification. It just
 * fetches the call_queue doc, joins the Agora channel, and shows a stripped
 * down call UI. Both participants are anonymized on this page — no names
 * are shown for privacy of psychology consultations.
 */
export const PsychologyJoinCallPage = () => {
  const { callId } = useParams();
  const { t, language } = useLanguage();
  useAuth(); // ensure auth context is mounted; cookies drive API auth
  const navigate = useNavigate();

  const [call, setCall] = useState(null);
  const [agoraConfig, setAgoraConfig] = useState(null);
  const [error, setError] = useState('');
  const [callEnded, setCallEnded] = useState(false);
  const [mediaState, setMediaState] = useState({ mic: true, cam: false, remoteJoined: false });
  const [permissionsGranted, setPermissionsGranted] = useState(() => {
    try { return localStorage.getItem('tababa_permissions_granted') === 'true'; } catch { return false; }
  });

  const clientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const joinedChannelRef = useRef(false);

  useEffect(() => {
    if (!callId) return;
    let cancelled = false;
    (async () => {
      try {
        const [callRes, cfgRes] = await Promise.all([
          axios.get(`${API}/calls/status/${callId}`, { withCredentials: true }),
          axios.get(`${API}/config`),
        ]);
        if (cancelled) return;
        setCall(callRes.data);
        setAgoraConfig(cfgRes.data);
        setMediaState((s) => ({ ...s, cam: callRes.data.call_type === 'video' }));
        // Notify doctor that the patient entered the room
        axios.post(`${API}/calls/${callId}/enter`, {}, { withCredentials: true }).catch(() => {});
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || t('فشل تحميل بيانات المكالمة', 'Failed to load call', 'Échec'));
      }
    })();
    const beforeUnload = () => {
      try {
        const url = `${API}/calls/${callId}/end`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([JSON.stringify({})], { type: 'application/json' }));
        }
      } catch { /* best effort */ }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', beforeUnload);
      cleanupAgora();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  useEffect(() => {
    if (!call || !agoraConfig || joinedChannelRef.current) return;
    if (callEnded || !permissionsGranted) return;
    joinedChannelRef.current = true;
    joinAgora(call.channel_name, call.call_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call, agoraConfig, permissionsGranted, callEnded]);

  const cleanupAgora = async () => {
    try {
      if (localTracksRef.current.audio) localTracksRef.current.audio.close();
      if (localTracksRef.current.video) localTracksRef.current.video.close();
      if (clientRef.current) await clientRef.current.leave();
    } catch (e) {
      console.warn('Agora cleanup error:', e);
    }
    localTracksRef.current = { audio: null, video: null };
    clientRef.current = null;
  };

  const joinAgora = async (channelName, callType) => {
    if (!agoraConfig?.agora_enabled || !agoraConfig?.agora_app_id) return;
    if (clientRef.current) return;
    let client;
    try {
      const uid = Math.floor(Math.random() * 1000000);
      const tokRes = await axios.get(
        `${API}/agora/token?channel=${channelName}&uid=${uid}`,
        { withCredentials: true },
      );
      const { token, app_id } = tokRes.data;
      client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;
      client.on('user-published', async (remoteUser, mediaType) => {
        await client.subscribe(remoteUser, mediaType);
        if (mediaType === 'video' && remoteVideoRef.current) {
          remoteUser.videoTrack.play(remoteVideoRef.current);
        }
        if (mediaType === 'audio') remoteUser.audioTrack.play();
        setMediaState((s) => ({ ...s, remoteJoined: true }));
      });
      client.on('user-unpublished', () => setMediaState((s) => ({ ...s, remoteJoined: false })));
      client.on('user-left', () => {
        setMediaState((s) => ({ ...s, remoteJoined: false }));
        axios.post(`${API}/calls/${callId}/end`, {}, { withCredentials: true }).catch(() => {});
      });
      await client.join(app_id, channelName, token, uid);
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      localTracksRef.current.audio = audioTrack;
      const tracksToPublish = [audioTrack];
      if (callType === 'video') {
        const videoTrack = await AgoraRTC.createCameraVideoTrack();
        localTracksRef.current.video = videoTrack;
        tracksToPublish.push(videoTrack);
        if (localVideoRef.current) videoTrack.play(localVideoRef.current);
      }
      await client.publish(tracksToPublish);
    } catch (e) {
      console.error('Agora join error:', e);
      setError(t('فشل الاتصال', 'Connection failed', 'Échec'));
      if (client) { try { await client.leave(); } catch { /* ignore */ } }
      clientRef.current = null;
      joinedChannelRef.current = false;
    }
  };

  const handleToggleMic = async () => {
    const tr = localTracksRef.current.audio;
    if (!tr) return;
    await tr.setEnabled(!mediaState.mic);
    setMediaState((s) => ({ ...s, mic: !s.mic }));
  };

  const handleToggleCam = async () => {
    const tr = localTracksRef.current.video;
    if (!tr) return;
    await tr.setEnabled(!mediaState.cam);
    setMediaState((s) => ({ ...s, cam: !s.cam }));
  };

  // 5-minutes-left ping: toast locally + tell the backend so BOTH sides get
  // an in-app + push notification (idempotent server-side).
  const handleSessionWarning = (remaining) => {
    const msg = remaining > 240
      ? t('تبقّى 5 دقائق على انتهاء الجلسة', '5 minutes left in this session', '5 minutes restantes')
      : t('الجلسة تقارب على الانتهاء', 'This session is about to end', 'La séance va se terminer');
    toast.warning(msg, { duration: 8000 });
    axios.post(`${API}/calls/${callId}/session-warning`, {}, { withCredentials: true }).catch(() => {});
  };

  const handleEndCall = async () => {
    try {
      await axios.post(`${API}/calls/${callId}/end`, {}, { withCredentials: true });
    } catch (e) {
      console.warn('End call failed:', e?.message || e);
    }
    await cleanupAgora();
    setCallEnded(true);
    setTimeout(() => navigate('/patient'), 1500);
  };

  if (!call) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="psychology-join-loading">
        {error ? (
          <Alert variant="destructive" className="max-w-md"><AlertDescription>{error}</AlertDescription></Alert>
        ) : (
          <Loader className="animate-spin text-[#1e88e5]" size={48} />
        )}
      </div>
    );
  }

  const isVideo = call.call_type === 'video';
  const doctorLabel = t('الطبيب', 'Doctor', 'Médecin');

  if (!permissionsGranted) {
    return (
      <ConsultationPermissionsGate
        videoRequired={isVideo}
        onGranted={() => setPermissionsGranted(true)}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-gray-100 ${language === 'ar' ? 'rtl' : 'ltr'}`} data-testid="psychology-join-call-page">
      <div className="container mx-auto px-4 py-4 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate('/patient')} className="mb-3" data-testid="back-to-patient">
          <ArrowLeft className={language === 'ar' ? 'ml-2 rotate-180' : 'mr-2'} size={20} />
          {t('العودة', 'Back', 'Retour')}
        </Button>

        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}

        {callEnded ? (
          <Card className="p-8 text-center max-w-md mx-auto" data-testid="psychology-call-ended">
            <div className="inline-flex w-20 h-20 bg-green-100 rounded-full items-center justify-center mb-4">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2c3e50] mb-2">{t('انتهت المكالمة', 'Call Ended', 'Terminé')}</h2>
            <p className="text-gray-600">{t('جاري العودة...', 'Returning...', 'Retour...')}</p>
          </Card>
        ) : (
          <Card className="overflow-hidden" data-testid="psychology-call-active">
            <div className="bg-[#0d47a1] text-white px-4 py-2 flex items-center justify-between gap-2">
              <p className="text-sm font-bold">{t('الجلسة النفسية', 'Psychology session', 'Séance')}</p>
              <SessionTimer
                elapsedSeconds={call.session_elapsed_seconds}
                minutes={call.session_minutes}
                onWarning={handleSessionWarning}
              />
            </div>
            {!agoraConfig?.agora_enabled && (
              <Alert className="rounded-none bg-yellow-50 border-yellow-200 border-b">
                <AlertDescription className="text-yellow-800 text-sm">
                  {t(
                    'إعدادات Agora غير مكتملة. سيبدأ العمل حالما يضيف المسؤول البيانات.',
                    'Agora not configured. Call will work once the admin adds credentials.',
                    'Agora non configuré.',
                  )}
                </AlertDescription>
              </Alert>
            )}

            {isVideo ? (
              <div className="relative bg-gray-900 aspect-video">
                <div ref={remoteVideoRef} className="w-full h-full flex items-center justify-center text-white">
                  {!mediaState.remoteJoined && (
                    <div className="text-center">
                      <div className="relative inline-flex w-24 h-24 mb-4">
                        <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                        <div className="relative w-24 h-24 bg-[#1e88e5] rounded-full flex items-center justify-center">
                          <Video size={48} />
                        </div>
                      </div>
                      <p className="text-lg" data-testid="psychology-waiting-doctor">
                        {t('في انتظار الطبيب...', 'Waiting for the doctor...', 'En attente du médecin...')}
                      </p>
                    </div>
                  )}
                </div>
                <div ref={localVideoRef} className="absolute bottom-4 right-4 w-32 h-24 bg-black rounded-lg overflow-hidden border-2 border-white"></div>
              </div>
            ) : (
              <div className="bg-gradient-to-br from-[#1565c0] to-[#1e88e5] py-16 text-center text-white">
                <div className="relative inline-flex w-32 h-32 mb-4">
                  <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                  <div className="relative w-32 h-32 bg-white/30 backdrop-blur rounded-full flex items-center justify-center">
                    <Phone size={56} />
                  </div>
                </div>
                {/* Privacy: psychology sessions never show the doctor's real name */}
                <p className="text-xl font-bold mb-1" data-testid="psychology-doctor-label">{doctorLabel}</p>
                <p className="text-blue-100">
                  {mediaState.remoteJoined
                    ? t('متصل', 'Connected', 'Connecté')
                    : t('جاري الاتصال...', 'Connecting...', 'Connexion...')}
                </p>
              </div>
            )}

            <div className="p-4 flex justify-center gap-3 bg-gray-50">
              <Button
                onClick={handleToggleMic}
                variant={mediaState.mic ? 'outline' : 'destructive'}
                size="lg"
                className="rounded-full w-14 h-14 p-0"
                data-testid="psychology-toggle-mic"
              >
                {mediaState.mic ? <Mic size={20} /> : <MicOff size={20} />}
              </Button>
              {isVideo && (
                <Button
                  onClick={handleToggleCam}
                  variant={mediaState.cam ? 'outline' : 'destructive'}
                  size="lg"
                  className="rounded-full w-14 h-14 p-0"
                  data-testid="psychology-toggle-cam"
                >
                  {mediaState.cam ? <Video size={20} /> : <VideoOff size={20} />}
                </Button>
              )}
              <Button
                onClick={handleEndCall}
                variant="destructive"
                size="lg"
                className="rounded-full w-14 h-14 p-0"
                data-testid="psychology-end-call"
              >
                <Phone size={20} className="rotate-[135deg]" />
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PsychologyJoinCallPage;
